import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { DocumentViewerComponent } from 'src/app/global/components/common/document-viewer/modal-image.component';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { CardType, FamilyLink, UpdateFamilyLinkPayload } from 'src/app/user/user.model';
import { FamilyLinkType } from '@bciti/types';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { finalize, map, mergeMap, takeUntil } from 'rxjs/operators';
import { OrganisationConfiguration } from 'src/app/global/global.model';

@Component({
    selector: 'app-step-confirm',
    templateUrl: './step-confirm.component.html',
    styleUrls: ['./step-confirm.component.scss'],
})
export class StepConfirmComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    @Input() selectedProfile: any;
    @Input() selectedFamilyMember: any;
    @Input() approvalGuidelines: string;

    public showApprovalGuide = false;
    public selectedProfileFamilyLinkWithSelectedMember: FamilyLink;
    public selectedFamilyMemberProfile: any;
    public highlightWarnings: boolean;
    public loading: boolean = true;
    public orgConfig: OrganisationConfiguration;

    constructor(
        public userService: UserService,
        private dialog: MatDialog,
        private store: Store<State>,
        private elementRef: ElementRef,
        private messageService: MessageService,
        private translateService: TranslateService,
    ) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.showApprovalGuide && !this.elementRef.nativeElement.querySelector('.files-header-row')?.contains(event.target)) {
            this.showApprovalGuide = false;
        }
    }

    ngOnInit(): void {
        // Load organization configuration
        this.loadOrganizationConfig();

        // Load family member profile and cards
        this.loadFamilyMemberProfile();

        // Load selected profile and cards if available
        if (this.selectedProfile && this.selectedProfile._id) {
            this.loadSelectedProfile();
        }
    }

    /**
     * Load organization configuration settings
     */
    private loadOrganizationConfig(): void {
        this.store
            .select(organisationConfigurationSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((config) => {
                this.orgConfig = config;
            });
    }

    /**
     * Load the family member profile and card information
     */
    private loadFamilyMemberProfile(): void {
        this.userService
            .getProfileById(this.selectedFamilyMember.userId._id)
            .pipe(
                takeUntil(this.destroy$),
                mergeMap((profile) => {
                    this.selectedFamilyMemberProfile = profile;
                    return this.loadCardWallet(profile._id, this.selectedFamilyMemberProfile);
                }),
                finalize(() => {
                    this.selectedProfileFamilyLinkWithSelectedMember = this.findFamilyLinkByUserId(this.selectedProfile._id);
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    /**
     * Load the selected profile and card information
     */
    private loadSelectedProfile(): void {
        this.userService
            .getProfileById(this.selectedProfile._id)
            .pipe(
                takeUntil(this.destroy$),
                mergeMap((profile) => {
                    this.selectedProfile = profile;
                    return this.loadCardWallet(profile._id, this.selectedProfile);
                }),
            )
            .subscribe();
    }

    /**
     * Load card wallet information for a profile
     * @param profileId The profile ID
     * @param profileToUpdate The profile object to update with card information
     */
    private loadCardWallet(profileId: string, profileToUpdate: any) {
        return this.userService.getWallet(profileId).pipe(
            map((wallet) => {
                if (wallet.cards) {
                    this.updateProfileWithCards(profileToUpdate, wallet.cards);
                }
                return profileToUpdate;
            }),
        );
    }

    /**
     * Update a profile with card information from wallet
     * @param profile The profile to update
     * @param cards The array of cards from the wallet
     */
    private updateProfileWithCards(profile: any, cards: any[]): void {
        profile.bcitiCard = cards.find((card) => card.cardType === CardType.BCITI);
        profile.legacyCard = cards.find((card) => card.cardType === CardType.LEGACY);
        profile.citizenCard = cards.find((card) => card.cardType === CardType.CITIZEN);
        profile.sportsCard = cards.find((card) => card.cardType === CardType.SPORTS);
        profile.libraryCard = cards.find((card) => card.cardType === CardType.LIBRARY);
    }

    /**
     * Find family link between selected member and user
     * @param userId The user ID to find the link for
     * @returns The family link object if found
     */
    private findFamilyLinkByUserId(userId: string): FamilyLink {
        if (!this.selectedFamilyMemberProfile?.family) return null;

        let foundFamilyLink: FamilyLink;

        for (const family of this.selectedFamilyMemberProfile.family) {
            for (const familyLinks of family?.links ?? []) {
                if (familyLinks?.userId?._id === userId) {
                    foundFamilyLink = familyLinks;
                    break;
                }
            }

            if (foundFamilyLink) break;
        }

        return foundFamilyLink;
    }

    /**
     * Open document viewer for uploaded documents
     * @param url The URL of the document to view
     */
    viewDocument(url: string): void {
        this.dialog.open(DocumentViewerComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            data: { src: url },
            panelClass: 'document-viewer-dialog',
        });
    }

    /**
     * Check if URL is an image
     * @param url The URL to check
     * @returns True if URL is an image file
     */
    urlIsImage(url: string): boolean {
        if (!url) return false;
        return ['.png', '.jpg', '.jpeg', '.gif'].indexOf(url.substring(url.lastIndexOf('.'))) > -1;
    }

    /**
     * Get the full name of a profile
     */
    getProfileFullName(profile: any): string {
        return `${profile.firstName} ${profile.lastName}`;
    }

    /**
     * Get the relationship translation key for the selected profile
     */
    getSelectedProfileRelationshipKey(): string {
        if (!this.selectedProfileFamilyLinkWithSelectedMember?.link) {
            return '';
        }
        return `pages.userFamily.relationshipText.${this.selectedProfileFamilyLinkWithSelectedMember.link.toLowerCase()}`;
    }

    /**
     * Get the relationship translation key for the family member
     */
    getFamilyMemberRelationshipKey(): string {
        if (!this.selectedFamilyMember?.link) {
            return '';
        }
        return `pages.userFamily.relationshipText.${this.selectedFamilyMember.link.toLowerCase()}`;
    }

    onSelectedProfileLinkChanged(newLink: FamilyLinkType): void {
        this.selectedProfileFamilyLinkWithSelectedMember.link = newLink;
        this.selectedFamilyMember.link = this.getReverseLinkType(newLink);
        this.submitLinkUpdate(this.selectedProfile._id, {
            targetUserId: this.selectedFamilyMember.userId._id,
            link: newLink,
            reference: this.getReverseLinkType(newLink),
        });
    }

    onFamilyMemberLinkChanged(newLink: FamilyLinkType): void {
        this.selectedFamilyMember.link = newLink;
        this.selectedProfileFamilyLinkWithSelectedMember.link = this.getReverseLinkType(newLink);
        this.submitLinkUpdate(this.selectedFamilyMemberProfile._id, {
            targetUserId: this.selectedProfile._id,
            link: newLink,
            reference: this.getReverseLinkType(newLink),
        });
    }

    private submitLinkUpdate(sourceId: string, payload: UpdateFamilyLinkPayload): void {
        this.userService
            .modifyFamilyLink(sourceId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.messageService.success(this.translateService.instant('pages.userFamily.edit.messages.success'));
                },
                (error) => {
                    this.messageService.error(error?.error?.messages?.[0], this.translateService.instant('pages.userFamily.edit.messages.error'));
                },
            );
    }

    private getReverseLinkType(link: FamilyLinkType): FamilyLinkType {
        switch (link) {
            case FamilyLinkType.CHILD:
                return FamilyLinkType.PARENT;
            case FamilyLinkType.PARENT:
                return FamilyLinkType.CHILD;
            case FamilyLinkType.SPOUSE:
                return FamilyLinkType.SPOUSE;
            case FamilyLinkType.OTHER:
                return FamilyLinkType.OTHER;
            default:
                return link;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
